import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { DsBadgeComponent, DsButtonDirective, DsCardDirective, DsProgressComponent } from '../ui';

interface PracticePuzzle {
  domain: 'Abstract' | 'Numerical' | 'Verbal' | 'Spatial';
  prompt: string;
  options: readonly string[];
  answer: number;
  explanation: string;
}

const PUZZLES: readonly PracticePuzzle[] = [
  {
    domain: 'Numerical',
    prompt: 'What comes next: 3, 7, 15, 31, ?',
    options: ['47', '55', '63', '64'],
    answer: 2,
    explanation: 'Each value is doubled and then increased by one.',
  },
  {
    domain: 'Verbal',
    prompt: 'Compass is to direction as thermometer is to ...',
    options: ['Weather', 'Temperature', 'Mercury', 'Heat'],
    answer: 1,
    explanation: 'Both instruments measure the property named in the answer.',
  },
  {
    domain: 'Abstract',
    prompt: 'A rule changes A1 to B3 and B2 to C4. What does C5 become?',
    options: ['D6', 'D7', 'E6', 'E7'],
    answer: 1,
    explanation: 'The letter advances by one and the number increases by two.',
  },
  {
    domain: 'Spatial',
    prompt: 'You face north, turn right, then make a half-turn. Which direction do you face?',
    options: ['North', 'East', 'South', 'West'],
    answer: 3,
    explanation: 'Right from north is east; a half-turn from east is west.',
  },
  {
    domain: 'Numerical',
    prompt: 'Which number does not belong: 8, 27, 64, 100, 125?',
    options: ['27', '64', '100', '125'],
    answer: 2,
    explanation: 'All except 100 are perfect cubes.',
  },
  {
    domain: 'Verbal',
    prompt: 'Which relationship best matches seed : tree?',
    options: ['Egg : bird', 'Leaf : branch', 'Rain : cloud', 'Wood : table'],
    answer: 0,
    explanation: 'A seed can develop into a tree as an egg can develop into a bird.',
  },
  {
    domain: 'Abstract',
    prompt: 'If every second symbol is removed from ◼ ● ▲ ◼ ● ▲, which sequence remains?',
    options: ['◼ ▲ ●', '● ◼ ▲', '◼ ◼ ▲', '● ● ▲'],
    answer: 0,
    explanation: 'Keeping positions one, three, and five leaves square, triangle, circle.',
  },
  {
    domain: 'Spatial',
    prompt:
      'A cube has red opposite blue and green opposite yellow. Which colour cannot touch red?',
    options: ['Blue', 'Green', 'Yellow', 'Unknown'],
    answer: 0,
    explanation: 'Opposite faces do not share an edge.',
  },
  {
    domain: 'Numerical',
    prompt: 'Complete the pair pattern: 2→6, 4→20, 6→42, 8→?',
    options: ['56', '64', '72', '80'],
    answer: 2,
    explanation: 'Multiply each number by the next integer: 8 × 9 = 72.',
  },
  {
    domain: 'Verbal',
    prompt: 'Which word is the closest opposite of scarce?',
    options: ['Rare', 'Hidden', 'Abundant', 'Small'],
    answer: 2,
    explanation: 'Scarce means insufficient or rare; abundant means plentiful.',
  },
  {
    domain: 'Abstract',
    prompt: 'Letters repeat ABC, numbers repeat 123. What is position 8?',
    options: ['A1', 'B2', 'B3', 'C2'],
    answer: 1,
    explanation: 'Position 8 is the second item in both repeating cycles: B2.',
  },
  {
    domain: 'Spatial',
    prompt: 'A page is rotated 90° clockwise twice. Where does its original top edge finish?',
    options: ['Top', 'Right', 'Bottom', 'Left'],
    answer: 2,
    explanation: 'Two quarter-turns equal a half-turn, moving the top edge to the bottom.',
  },
];

@Component({
  selector: 'app-practice-page',
  imports: [DsBadgeComponent, DsButtonDirective, DsCardDirective, DsProgressComponent],
  templateUrl: './practice-page.html',
  styleUrl: './practice-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticePage {
  protected readonly mode = signal<'daily' | 'practice'>('daily');
  protected readonly domain = signal<PracticePuzzle['domain'] | 'All'>('All');
  protected readonly index = signal(0);
  protected readonly selected = signal<number | null>(null);
  protected readonly correct = signal(0);
  protected readonly attempted = signal(0);
  protected readonly dailyIndex = this.dayNumber() % PUZZLES.length;
  protected readonly available = computed(() =>
    this.domain() === 'All' ? PUZZLES : PUZZLES.filter((puzzle) => puzzle.domain === this.domain()),
  );
  protected readonly puzzle = computed(() =>
    this.mode() === 'daily'
      ? PUZZLES[this.dailyIndex]
      : this.available()[this.index() % this.available().length],
  );

  protected choose(option: number): void {
    if (this.selected() !== null) return;
    this.selected.set(option);
    this.attempted.update((value) => value + 1);
    if (option === this.puzzle().answer) this.correct.update((value) => value + 1);
  }

  protected next(): void {
    this.selected.set(null);
    this.index.update((value) => value + 1);
  }
  protected setMode(mode: 'daily' | 'practice'): void {
    this.mode.set(mode);
    this.selected.set(null);
    this.index.set(0);
  }
  protected setDomain(domain: PracticePuzzle['domain'] | 'All'): void {
    this.domain.set(domain);
    this.selected.set(null);
    this.index.set(0);
  }
  private dayNumber(): number {
    return Math.floor(Date.now() / 86_400_000);
  }
}
